// La Mine aux briques (Lot C, T5) : cache CLIENT des cosmétiques équipés + les tables PURES qui
// traduisent un loot en effet visuel (couleur de traînée, libellé de titre). Tout est COSMÉTIQUE
// et hors simulation : rien ici n'entre dans le hash d'état ni le rejeu réseau. Le cache est
// alimenté par apiMineState (au login et après chaque équipement) et lu de façon SYNCHRONE par le
// rendu (particules), le solo (pas/victoire) et la Mine (titre). Hors ligne / non connecté : le
// cache reste vide -> tout equipped() renvoie null -> effets PAR DÉFAUT (dégradation silencieuse).
import { apiMineState } from './api'
import { lootById, type LootSlot } from '../shared/loots'

// slot -> loot_id équipé. Vide au démarrage ; jamais partagé avec la sim.
let cache: Record<string, string> = {}

/** Charge l'équipement courant depuis le serveur (authentifié). Best-effort : hors ligne / non
 *  connecté, on garde le cache tel quel (vide -> effets par défaut), sans jamais lever. */
export async function loadLoadout(): Promise<void> {
  try {
    const s = await apiMineState()
    cache = { ...s.equipped }
  } catch {
    /* hors ligne / non connecté : on ne touche pas au cache */
  }
}

/** Loot équipé sur `slot`, ou null (rien d'équipé / hors ligne). Lecture SYNCHRONE (rendu). */
export function equipped(slot: LootSlot): string | null {
  return cache[slot] ?? null
}

/** Maj OPTIMISTE du cache après un apiEquip réussi (lootId null = déséquipe). C'est ce qui fait
 *  refléter l'effet live (particules/titre) sans réattendre le réseau. */
export function setEquippedLocal(slot: LootSlot, lootId: string | null): void {
  if (lootId === null) delete cache[slot]
  else cache[slot] = lootId
}

// --- Tables PURES loot -> effet (testées : loadout.test.ts). Renvoient un TABLEAU de couleurs
// (les gravats arc-en-ciel / gemmes sont multicolores) ; [] = pas d'effet, on garde le défaut.

/** Palette de la traînée de DASH selon le loot `trailDash` équipé. [] = traînée par défaut. */
export function trailDashColors(lootId: string | null): string[] {
  switch (lootId) {
    case 'trail-poussiere': return ['#f0c850', '#e0a838'] // poussière dorée
    case 'trail-feuilles': return ['#5fbf4f', '#8fd36a']   // feuilles vertes
    case 'trail-etincelles': return ['#ffffff', '#cfe8ff'] // étincelles blanches crépitantes
    // --- Vague 0 ---
    case 'trail-braises': return ['#ff6b1a', '#ffb347']    // braises ardentes orangées
    case 'trail-encre': return ['#2b2b4a', '#4a4a7a']      // encre bleu nuit
    case 'trail-bulles': return ['#a8e6ff', '#d6f5ff']     // bulles bleu clair
    case 'trail-petales': return ['#ff9ec7', '#ffd1e6']    // pétales rosés
    case 'trail-spectrale': return ['#8affd6', '#d0fff0']  // sillage spectral vert d'eau
    case 'trail-foudre': return ['#fff36b', '#ffffff']     // éclairs jaune électrique
    case 'trail-stellaire': return ['#ffe27a', '#c9a0ff', '#ffffff'] // comète d'étoiles (multicolore)
    // --- Vague du 05/08 (planche cosmetiques2, en attente de validation) ---
    case 'trail-notes': return ['#ffd23f', '#fff0a8']      // notes de musique dorées
    case 'trail-papillons': return ['#8fd0f0', '#f0a0c8', '#ffe27a'] // papillons pastel
    default: return []
  }
}

/* --- LES GLYPHES (vague du 05/08). Jusqu'ici une traînée ne changeait que la
   COULEUR des particules ; ces trois loots changent leur FORME. La table est
   pure et séparée des palettes : un loot à glyphe garde sa palette (le glyphe
   prend la couleur du cran, exactement comme la croix qu'il remplace). */

export type TrailGlyphe = 'note' | 'papillon' | 'confetti'

/** Forme des particules de DASH selon le loot équipé. null = croix habituelle. */
export function trailDashGlyphe(lootId: string | null): TrailGlyphe | null {
  switch (lootId) {
    case 'trail-notes': return 'note'
    case 'trail-papillons': return 'papillon'
    default: return null
  }
}

/** Forme des gravats de CREUSAGE selon le loot équipé. null = carrés habituels. */
export function trailDigGlyphe(lootId: string | null): TrailGlyphe | null {
  switch (lootId) {
    case 'gravats-confettis': return 'confetti'
    default: return null
  }
}

/** Palette des gravats de CREUSAGE selon le loot `trailDig` équipé. [] = gravats par défaut. */
export function trailDigColors(lootId: string | null): string[] {
  switch (lootId) {
    case 'gravats-arcenciel': return ['#ff5f57', '#ffb13d', '#f7d54a', '#5fbf4f', '#5db4ff', '#b98bff'] // arc-en-ciel
    case 'gravats-gemmes': return ['#6afcff', '#b98bff'] // gemmes cyan / violet
    case 'gravats-neige': return ['#ffffff', '#dbeeff']  // neige
    // --- Vague 0 ---
    case 'gravats-charbon': return ['#3a3a3a', '#5a5a5a']  // charbon anthracite
    case 'gravats-sable': return ['#e8c887', '#d4a95f']    // sable doré
    case 'gravats-cendres': return ['#8a8a8a', '#b0b0b0']  // cendres grises
    case 'gravats-emeraude': return ['#2ec76b', '#5fe89a'] // émeraude verte
    case 'gravats-lave': return ['#ff3a1a', '#ff8c42', '#ffd23f'] // lave (multicolore chaud)
    case 'gravats-cristal': return ['#a0f0ff', '#e0ffff']  // cristal cyan clair
    case 'gravats-poussiere-etoiles': return ['#ffe27a', '#c9a0ff', '#8affd6', '#ffffff'] // poussière d'étoiles (multicolore)
    // --- Vague du 05/08 (planche cosmetiques2, en attente de validation) ---
    case 'gravats-confettis': return ['#ff5f57', '#ffd23f', '#5fbf4f', '#5db4ff', '#f0a0c8'] // confettis
    default: return []
  }
}

/** Libellé du TITRE équipé, préfixe « Titre : » retiré (ex. « Mineur d'élite »), ou null si
 *  aucun titre / loot inconnu / mauvais slot. Pur (lecture du catalogue). */
export function titleLabel(lootId: string | null): string | null {
  if (!lootId) return null
  const l = lootById(lootId)
  if (!l || l.slot !== 'title') return null
  return l.name.replace(/^Titre\s*:\s*/, '')
}
